import { useCallback, useEffect, useMemo, useState, type MutableRefObject } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useSyncBranchSettingsOnFocus } from '../../../shell/session/hooks/useSyncBranchSettingsOnFocus';
import { useScreenLoad } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import type { ScreenRefreshResult } from '../../../shared/ui/screenRefreshFeedback';
import type { CategoryRecord } from '../../../catalog/domain-api';
import { preflightCatalogSnapshotForRead as preflightPosCatalogSnapshot, invalidateMenuOptionsMemoryCache } from '../../../catalog/infra-api';
import { usePosCashSessionLoader } from './usePosCashSessionLoader';
import { loadPosCategories } from './posCategoryLoader';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import { buildCategoryOptions } from '../../../catalog/domain-api';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';
import { homeDbg } from '../../../shared/dev/homeDebug';
import { refreshPosLocalSurfaces } from '../../application/refreshPosLocalSurfaces';
import { usePosLiveCatalogProbe } from '../../application/usePosLiveCatalogProbe';

const EMPTY_CATEGORY_FALLBACK: CategoryRecord[] = [];

type CatalogActionsRef = MutableRefObject<{
  reloadCatalog: () => void | Promise<void>;
  reloadFilterOptions: () => void;
}>;

type Params = {
  currentBranchId: string | null | undefined;
  routeName: string;
  catalogActionsRef: CatalogActionsRef;
  reloadPromotionsFromLocal: () => void;
  refreshPriceLists?: () => void | Promise<void>;
};

export function usePosOperationalPane({
  currentBranchId,
  routeName,
  catalogActionsRef,
  reloadPromotionsFromLocal,
  refreshPriceLists,
}: Params) {
  const isFocused = useIsFocused();
  usePosLiveCatalogProbe(isFocused);
  const [categories, setCategories] = useState<CategoryRecord[]>([]);

  const refreshCategories = useCallback(
    () => loadPosCategories(setCategories, () => true, { forceRemoteSync: true }),
    []
  );

  const refreshLocalCategories = useCallback(
    () => loadPosCategories(setCategories, () => true),
    []
  );

  const refreshLocalSurfaces = useCallback(() => {
    refreshPosLocalSurfaces({
      catalog: () =>
        Promise.resolve(catalogActionsRef.current.reloadCatalog()).catch(
          captureCatch('pos', 'pane-reload-catalog'),
        ),
      categories: refreshLocalCategories,
      filters: () => catalogActionsRef.current.reloadFilterOptions(),
      promotions: reloadPromotionsFromLocal,
      priceLists: refreshPriceLists,
      menuOptions: invalidateMenuOptionsMemoryCache,
    });
  }, [catalogActionsRef, refreshLocalCategories, refreshPriceLists, reloadPromotionsFromLocal]);

  const { refreshCashSession } = usePosCashSessionLoader(currentBranchId ?? null);

  const loadFn = useCallback(async (opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
    if (opts?.force) {
      const online = await isOnline().catch(() => false);
      let partialMessage: string | undefined;

      if (online) {
        await preflightPosCatalogSnapshot({ force: true });
      }

      const { reloadCatalog } = catalogActionsRef.current;

      await Promise.all([
        refreshCategories(),
        reloadCatalog(),
        refreshCashSession(true, true),
      ]);
      reloadPromotionsFromLocal();
      catalogActionsRef.current.reloadFilterOptions();
      void Promise.resolve(refreshPriceLists?.());
      invalidateMenuOptionsMemoryCache();

      return partialMessage ? { partialMessage } : undefined;
    } else {
      if (!currentBranchId) return;
      homeDbg('POS.pane', 'soft load');
      void preflightPosCatalogSnapshot().catch(captureCatch('pos', 'pane-preflight-catalog'));
      refreshLocalSurfaces();
    }
  }, [
    catalogActionsRef,
    currentBranchId,
    refreshCashSession,
    refreshCategories,
    refreshLocalSurfaces,
    refreshPriceLists,
    reloadPromotionsFromLocal,
  ]);

  const { refreshing, onRefresh: handleRefresh } = useScreenLoad(
    routeName,
    loadFn
  );

  useEffect(() => {
    if (!isFocused) return;
    let mounted = true;
    homeDbg('POS.pane', 'mount categories load');
    void loadPosCategories(setCategories, () => mounted);
    return () => {
      mounted = false;
    };
  }, [isFocused, setCategories]);

  useEffect(() => {
    const unsubscribeCatalogSynced = subscribeCatalogSynced(() => {
      invalidateMenuOptionsMemoryCache();
      if (!isFocused) {
        homeDbg('POS.pane', 'catalogSynced skip UI — blurred');
        return;
      }
      void loadPosCategories(setCategories, () => true);
      catalogActionsRef.current.reloadFilterOptions();
      reloadPromotionsFromLocal();
      void Promise.resolve(refreshPriceLists?.());
    });
    return unsubscribeCatalogSynced;
  }, [catalogActionsRef, isFocused, refreshPriceLists, reloadPromotionsFromLocal, setCategories]);

  useSyncBranchSettingsOnFocus();

  const importCategoryOptions = useMemo(
    () =>
      buildCategoryOptions(categories, {
        fallbackCategories: EMPTY_CATEGORY_FALLBACK,
        forItemType: 'physical',
      }).map((option) => ({ value: option.id, label: option.label })),
    [categories]
  );

  return {
    categories,
    setCategories,
    refreshing,
    handleRefresh,
    importCategoryOptions,
  };
}
